import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { X, Plus, Trash2, Edit3, Save, XCircle } from 'lucide-react';
import ConfirmationModal from './ConfirmationModal';
import useConfirmation from '../hooks/useConfirmation';
import Portal from './Portal';
import { useTransactionTypes } from '../contexts/TransactionTypesContext';
import { updateTransactionTypesAggregationOnCreate, updateTransactionTypesAggregationOnUpdate, updateTransactionTypesAggregationOnDelete } from '../utils/transactionTypesAggregations';

const TransactionTypesModal = ({ isOpen, onClose }) => {
  const { transactionTypes: contextTypes } = useTransactionTypes();
  const [transactionTypes, setTransactionTypes] = useState(contextTypes);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    categoria: 'receita' // receita ou despesa
  });

  const { confirmation, showConfirmation, hideConfirmation, confirmAction } = useConfirmation();

  useEffect(() => {
    // Usar dados do contexto se disponíveis
    if (contextTypes && contextTypes.length > 0) {
      setTransactionTypes(contextTypes);
    } else if (isOpen) {
      // Se contexto não tem dados, buscar diretamente
      fetchTransactionTypes();
    }
  }, [isOpen, contextTypes]);

  // Travar scroll do fundo quando o modal Tipos de Transação estiver aberto
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      const prevHtmlOverflow = document.documentElement.style.overflow;
      const prevBodyOverflow = document.body.style.overflow;
      const prevBodyPosition = document.body.style.position;
      const prevBodyTop = document.body.style.top;
      const prevBodyWidth = document.body.style.width;
      const prevBodyHeight = document.body.style.height;

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      return () => {
        document.documentElement.style.overflow = prevHtmlOverflow;
        document.body.style.overflow = prevBodyOverflow;
        document.body.style.position = prevBodyPosition;
        document.body.style.top = prevBodyTop;
        document.body.style.width = prevBodyWidth;
        document.body.style.height = prevBodyHeight;
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const fetchTransactionTypes = async () => {
    // Não precisa mais buscar diretamente, o contexto já fornece os dados do agregado
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const typeData = {
        nome: formData.nome.toUpperCase(),
        categoria: formData.categoria,
        criadoEm: new Date()
      };

      const docRef = await addDoc(collection(db, 'tiposTransacao'), typeData);
      
      // Buscar tipo criado do Firestore
      const newTypeDoc = await getDoc(doc(db, 'tiposTransacao', docRef.id));
      if (newTypeDoc.exists()) {
        const newTypeData = {
          id: newTypeDoc.id,
          ...newTypeDoc.data()
        };
        
        // Atualizar agregado
        try {
          await updateTransactionTypesAggregationOnCreate(newTypeData);
        } catch (err) {
          // Erro ao atualizar agregado de tipos de transação
          // Não bloquear a operação principal, mas o agregado será recarregado pelo contexto
        }
      }

      setFormData({ nome: '', categoria: 'receita' });
      setShowAddForm(false);
      // O listener em tempo real atualizará automaticamente
    } catch (error) {
      // Erro ao salvar tipo de transação
    }
  };

  const handleDelete = async (id) => {
    if (!id) {
      alert('Erro: ID do tipo de transação não fornecido');
      return;
    }
    
    const confirmed = await showConfirmation({
      title: 'Excluir Tipo de Transação',
      messidade: 'Tem certeza que deseja excluir este tipo de transação? Esta ação não pode ser desfeita.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });
    
    if (!confirmed) {
      return;
    }
    
    try {
      const typeRef = doc(db, 'tiposTransacao', id);
      
      // Verificar se o documento existe antes de deletar
      const typeDoc = await getDoc(typeRef);
      if (!typeDoc.exists()) {
        alert(`Erro: Tipo de transação não encontrado (ID: ${id})`);
        return;
      }
      
      // Deletar o documento
      await deleteDoc(typeRef);
      
      // Atualizar agregado
      try {
        await updateTransactionTypesAggregationOnDelete(id);
      } catch (err) {
        // Não bloquear a operação principal, mas o agregado será recarregado pelo contexto
      }
      
      // Atualizar estado local imediatamente
      setTransactionTypes(prevTypes => prevTypes.filter(t => t.id !== id));
      
      // O listener em tempo real atualizará automaticamente
    } catch (error) {
      alert(`Erro ao excluir tipo de transação: ${error.message || 'Erro desconhecido'}\n\nCódigo: ${error.code || 'N/A'}`);
    }
  };

  const startEdit = (type) => {
    setEditingId(type.id);
    setFormData({
      nome: type.nome,
      categoria: type.categoria
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ nome: '', categoria: 'revenue' });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      const typeRef = doc(db, 'tiposTransacao', editingId);
      await updateDoc(typeRef, {
        nome: formData.nome.toUpperCase(),
        categoria: formData.categoria,
        atualizadoEm: new Date()
      });

      // Buscar tipo atualizado do Firestore
      const updatedTypeDoc = await getDoc(doc(db, 'tiposTransacao', editingId));
      if (updatedTypeDoc.exists()) {
        const updatedTypeData = {
          id: updatedTypeDoc.id,
          ...updatedTypeDoc.data()
        };
        
        // Atualizar agregado
        try {
          await updateTransactionTypesAggregationOnUpdate(updatedTypeData);
        } catch (err) {
          // Erro ao atualizar agregado de tipos de transação
          // Não bloquear a operação principal, mas o agregado será recarregado pelo contexto
        }
      }

      cancelEdit();
      // O listener em tempo real atualizará automaticamente
    } catch (error) {
      // Erro ao editar tipo de transação
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        className="modal-backdrop fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[99999]"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl">
          <div className="relative flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Tipos de Transação
            </h2>
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <button
                onClick={() => setShowAddForm(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar Tipo</span>
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-900 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">

            {/* Formulário de adição */}
            {showAddForm && (
              <div className="card mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Novo Tipo de Transação
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Nome do Tipo</label>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value.toUpperCase() })}
                        className="input-field"
                        placeholder="Ex: ALUGUEL"
                        required
                      />
                    </div>

                    <div>
                      <label className="label">Categoria</label>
                      <select
                        value={formData.categoria}
                        onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                        className="input-field"
                      >
                        <option value="receita">Receita</option>
                        <option value="despesa">Despesa</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddForm(false);
                        setFormData({ nome: '', categoria: 'receita' });
                      }}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      Salvar
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Lista de tipos de transação */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {transactionTypes.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-600 mb-4">
                      <Plus className="w-16 h-16 mx-auto mb-4" />
                      <p>Nenhum tipo de transação cadastrado</p>
                      <p className="text-sm">Clique em "Adicionar Tipo" para começar</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Coluna de Receitas */}
                    <div>
                      <div className="space-y-3">
                        {transactionTypes
                          .filter(type => type.categoria === 'receita')
                          .map((type) => (
                            <div key={type.id} className="card py-3">
                              {editingId === type.id ? (
                                // Formulário de edição
                                <form onSubmit={handleEditSubmit} className="space-y-4">
                                  <div className="grid grid-cols-1 gap-4">
                                    <div>
                                      <label className="label">Nome do Tipo</label>
                                      <input
                                        type="text"
                                        value={formData.nome}
                                        onChange={(e) => setFormData({ ...formData, nome: e.target.value.toUpperCase() })}
                                        className="input-field"
                                        placeholder="Ex: FATURAMENTO"
                                        required
                                      />
                                    </div>

                                    <div>
                                      <label className="label">Categoria</label>
                                      <select
                                        value={formData.categoria}
                                        onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                                        className="input-field"
                                      >
                                        <option value="revenue">Receita</option>
                                        <option value="expense">Despesa</option>
                                      </select>
                                    </div>
                                  </div>

                                  <div className="flex space-x-3 justify-end">
                                    <button
                                      type="button"
                                      onClick={cancelEdit}
                                      className="btn-secondary flex items-center space-x-2"
                                    >
                                      <XCircle className="w-4 h-4" />
                                      <span>Cancelar</span>
                                    </button>
                                    <button
                                      type="submit"
                                      className="btn-primary flex items-center space-x-2"
                                    >
                                      <Save className="w-4 h-4" />
                                      <span>Salvar</span>
                                    </button>
                                  </div>
                                </form>
                              ) : (
                                // Visualização normal
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3 flex-1">
                                    <h4 className="font-semibold text-gray-900">
                                      {type.nome}
                                    </h4>
                                    <span
                                      className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                                        type.categoria === 'receita'
                                          ? 'bg-success-100 text-success-800'
                                          : 'bg-danger-100 text-danger-800'
                                      }`}
                                    >
                                      {type.categoria === 'receita' ? 'Receita' : 'Despesa'}
                                    </span>
                                  </div>
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => startEdit(type)}
                                      className="p-1 text-gray-600 hover:text-primary-600 transition-colors"
                                      title="Editar"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(type.id)}
                                      className="p-1 text-gray-600 hover:text-danger-600 transition-colors"
                                      title="Excluir"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        {transactionTypes.filter(type => type.categoria === 'receita').length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma receita cadastrada</p>
                        )}
                      </div>
                    </div>

                    {/* Coluna de Despesas */}
                    <div>
                      <div className="space-y-3">
                        {transactionTypes
                          .filter(type => type.categoria === 'despesa')
                          .map((type) => (
                            <div key={type.id} className="card py-3">
                              {editingId === type.id ? (
                                // Formulário de edição
                                <form onSubmit={handleEditSubmit} className="space-y-4">
                                  <div className="grid grid-cols-1 gap-4">
                                    <div>
                                      <label className="label">Nome do Tipo</label>
                                      <input
                                        type="text"
                                        value={formData.nome}
                                        onChange={(e) => setFormData({ ...formData, nome: e.target.value.toUpperCase() })}
                                        className="input-field"
                                        placeholder="Ex: ALUGUEL"
                                        required
                                      />
                                    </div>

                                    <div>
                                      <label className="label">Categoria</label>
                                      <select
                                        value={formData.categoria}
                                        onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                                        className="input-field"
                                      >
                                        <option value="revenue">Receita</option>
                                        <option value="expense">Despesa</option>
                                      </select>
                                    </div>
                                  </div>

                                  <div className="flex space-x-3 justify-end">
                                    <button
                                      type="button"
                                      onClick={cancelEdit}
                                      className="btn-secondary flex items-center space-x-2"
                                    >
                                      <XCircle className="w-4 h-4" />
                                      <span>Cancelar</span>
                                    </button>
                                    <button
                                      type="submit"
                                      className="btn-primary flex items-center space-x-2"
                                    >
                                      <Save className="w-4 h-4" />
                                      <span>Salvar</span>
                                    </button>
                                  </div>
                                </form>
                              ) : (
                                // Visualização normal
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3 flex-1">
                                    <h4 className="font-semibold text-gray-900">
                                      {type.nome}
                                    </h4>
                                    <span
                                      className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                                        type.categoria === 'receita'
                                          ? 'bg-success-100 text-success-800'
                                          : 'bg-danger-100 text-danger-800'
                                      }`}
                                    >
                                      {type.categoria === 'receita' ? 'Receita' : 'Despesa'}
                                    </span>
                                  </div>
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => startEdit(type)}
                                      className="p-1 text-gray-600 hover:text-primary-600 transition-colors"
                                      title="Editar"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(type.id)}
                                      className="p-1 text-gray-600 hover:text-danger-600 transition-colors"
                                      title="Excluir"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        {transactionTypes.filter(type => type.categoria === 'despesa').length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma despesa cadastrada</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Confirmação - Renderizado separadamente */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        onClose={hideConfirmation}
        onConfirm={() => confirmAction(confirmation.onConfirm)}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
      />
    </Portal>
  );
};

export default TransactionTypesModal;
